import { motion } from "framer-motion";
import { ArrowUpRight, TrendingUp } from "lucide-react";

const projects = [
  {
    tag: "Local Business",
    title: "Local Business Website Redesign",
    desc: "Rebuilt a service business site with local SEO, service-area pages, and click-to-call flows.",
    metric: "+48% more leads",
    span: "md:col-span-7",
    kind: "image",
    image:
      "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1400&q=75",
    accent: "from-apex-blue/40 via-apex-purple/20 to-transparent",
  },
  {
    tag: "SEO",
    title: "SEO Growth Campaign",
    desc: "Content clusters, technical SEO cleanup, and internal linking to unlock long-tail traffic.",
    metric: "+65% organic traffic",
    span: "md:col-span-5",
    kind: "seo-mock",
    accent: "from-apex-cyan/45 via-apex-blue/25 to-apex-navyDeep",
  },
  {
    tag: "SaaS",
    title: "SaaS Landing Page",
    desc: "Conversion-focused landing page with animated product tour and dual-CTA hero.",
    metric: "2.3x higher conversion",
    span: "md:col-span-5",
    kind: "saas-mock",
    accent: "from-apex-purple/50 via-apex-blue/25 to-apex-navyDeep",
  },
  {
    tag: "E-commerce",
    title: "E-commerce Conversion Upgrade",
    desc: "PDP redesign, checkout simplification, and cart-abandonment lifecycle emails.",
    metric: "+31% revenue per session",
    span: "md:col-span-7",
    kind: "image",
    image:
      "https://images.unsplash.com/photo-1483985988355-763728e1935b?auto=format&fit=crop&w=1400&q=75",
    accent: "from-apex-orange/40 via-apex-purple/25 to-transparent",
  },
];

export default function Portfolio() {
  return (
    <section id="work" data-testid="portfolio-section" className="section-pad relative">
      <div className="container-apex">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <div className="eyebrow mb-4"><span className="w-6 h-px bg-apex-cyan" /> Selected work</div>
            <h2 className="text-4xl md:text-5xl font-extrabold leading-[1.05]">
              Real projects. <span className="text-apex-orange">Real numbers.</span>
            </h2>
          </div>
          <p className="text-white/70 max-w-md md:text-right">
            A snapshot of the design and SEO work we've shipped for founders, teams, and marketing leaders.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-6">
          {projects.map((p, i) => (
            <motion.article
              key={p.title}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.75, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6 }}
              className={`glass glass-inner-glow p-0 overflow-hidden group relative ${p.span}`}
              data-testid={`project-card-${i}`}
            >
              <div className="relative h-64 md:h-72 overflow-hidden">
                {p.kind === "image" ? (
                  <>
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-[1400ms] group-hover:scale-[1.06]"
                      style={{ backgroundImage: `url(${p.image})` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-apex-navy via-apex-navy/60 to-transparent" />
                  </>
                ) : p.kind === "seo-mock" ? (
                  <SeoCaseMock accent={p.accent} />
                ) : p.kind === "saas-mock" ? (
                  <SaasCaseMock accent={p.accent} />
                ) : (
                  <div className={`absolute inset-0 bg-gradient-to-br ${p.accent}`} />
                )}

                {/* Metric badge */}
                <div className="absolute left-5 top-5 inline-flex items-center gap-2 rounded-full bg-black/40 backdrop-blur-md border border-white/15 px-3 py-1.5 text-xs font-semibold">
                  <span className="w-1.5 h-1.5 rounded-full bg-apex-orange" />
                  {p.metric}
                </div>
                <div className="absolute right-5 top-5 inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 px-3 py-1 text-[11px] uppercase tracking-widest">
                  {p.tag}
                </div>
              </div>

              <div className="p-6 md:p-7 flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-heading font-bold text-xl leading-tight">{p.title}</h3>
                  <p className="mt-2 text-sm text-white/65 leading-relaxed max-w-xl">{p.desc}</p>
                </div>
                <a href="#contact" className="shrink-0 mt-1 w-10 h-10 rounded-full border border-white/15 bg-white/[0.04] flex items-center justify-center hover:bg-apex-orange hover:border-apex-orange transition-colors group/link" data-testid={`project-link-${i}`}>
                  <ArrowUpRight className="w-4 h-4 text-white/90" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------- Sophisticated CSS/SVG mockups for portfolio cards --------- */

function SeoCaseMock({ accent }) {
  const bars = [12, 20, 18, 28, 24, 34, 38, 46, 52, 60, 66, 72];
  return (
    <div className={`absolute inset-0 bg-gradient-to-br ${accent}`}>
      <div className="absolute inset-0 grid-lines opacity-20" />
      <div className="absolute inset-x-6 top-6 rounded-2xl border border-white/15 bg-white/[0.04] backdrop-blur-md p-4">
        <div className="flex items-center justify-between text-[10px] uppercase tracking-widest">
          <span className="text-white/60">Organic Sessions</span>
          <span className="text-emerald-400 font-semibold">▲ 142%</span>
        </div>
        <svg viewBox="0 0 260 70" className="w-full h-16 mt-2">
          <defs>
            <linearGradient id="pf-line" x1="0" x2="1">
              <stop offset="0%" stopColor="#23D3FF" />
              <stop offset="100%" stopColor="#7C3AED" />
            </linearGradient>
            <linearGradient id="pf-area" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#246BFE" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#246BFE" stopOpacity="0" />
            </linearGradient>
          </defs>
          <motion.path
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1 }}
            d="M0,58 C25,50 45,54 70,44 C100,30 130,36 160,24 C190,14 220,18 260,6 L260,70 L0,70 Z"
            fill="url(#pf-area)"
          />
          <motion.path
            initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.6, ease: [0.22,1,0.36,1] }}
            d="M0,58 C25,50 45,54 70,44 C100,30 130,36 160,24 C190,14 220,18 260,6"
            fill="none" stroke="url(#pf-line)" strokeWidth="2.4" strokeLinecap="round"
          />
        </svg>
      </div>

      <div className="absolute inset-x-6 bottom-6 grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-white/12 bg-white/[0.03] backdrop-blur-md p-2.5">
          <div className="text-[9px] uppercase tracking-widest text-white/50">Keywords ranking</div>
          <div className="font-heading font-bold text-white text-lg">1,284</div>
          <div className="flex gap-0.5 mt-1.5 items-end h-4">
            {bars.map((h, i) => (
              <div key={i} style={{ height: `${h * 0.2}px` }} className="w-1 rounded-sm bg-gradient-to-t from-apex-blue to-apex-cyan" />
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-white/12 bg-white/[0.03] backdrop-blur-md p-2.5">
          <div className="text-[9px] uppercase tracking-widest text-white/50">Domain authority</div>
          <div className="font-heading font-bold text-white text-lg">58 → <span className="text-apex-cyan">71</span></div>
          <div className="mt-2 h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full w-[71%] bg-gradient-to-r from-apex-cyan to-apex-orange rounded-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

function SaasCaseMock({ accent }) {
  return (
    <div className={`absolute inset-0 bg-gradient-to-br ${accent}`}>
      <div className="absolute inset-0 grid-lines opacity-20" />
      <div className="absolute inset-x-6 top-6 rounded-2xl border border-white/15 bg-white/[0.05] backdrop-blur-md overflow-hidden">
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 bg-white/[0.03]">
          <span className="w-2 h-2 rounded-full bg-red-400/70" />
          <span className="w-2 h-2 rounded-full bg-yellow-400/70" />
          <span className="w-2 h-2 rounded-full bg-emerald-400/70" />
          <div className="ml-3 flex-1 h-4 rounded-md bg-white/[0.05] flex items-center px-2 text-[9px] tracking-wider text-white/50 truncate">app.trellis.com / try-free</div>
        </div>
        <div className="p-4 grid grid-cols-5 gap-3">
          <div className="col-span-3">
            <div className="text-[10px] uppercase tracking-widest text-apex-cyan">Introducing</div>
            <div className="mt-1 h-2 w-4/5 rounded bg-white" />
            <div className="mt-1.5 h-2 w-3/5 rounded bg-apex-orange" />
            <div className="mt-2 h-1.5 w-full rounded bg-white/25" />
            <div className="mt-1 h-1.5 w-5/6 rounded bg-white/20" />
            <div className="mt-3 flex gap-2">
              <div className="h-5 w-16 rounded-md bg-apex-orange flex items-center justify-center text-[8px] font-bold text-white">Try Free</div>
              <div className="h-5 w-14 rounded-md border border-white/25 flex items-center justify-center text-[8px] text-white/85">Demo</div>
            </div>
          </div>
          <div className="col-span-2 rounded-lg border border-white/12 bg-white/[0.04] p-2 space-y-1">
            <div className="h-1.5 w-full rounded bg-white/15" />
            <div className="h-1.5 w-4/5 rounded bg-white/15" />
            <div className="h-1.5 w-3/5 rounded bg-white/15" />
            <div className="h-3 w-full rounded bg-apex-cyan/70 mt-1" />
          </div>
        </div>
      </div>

      <div className="absolute right-6 bottom-6 flex items-center gap-2 rounded-full bg-black/45 backdrop-blur-md border border-white/15 px-3 py-1.5 text-[11px] font-semibold text-white">
        <TrendingUp className="w-3.5 h-3.5 text-apex-orange" strokeWidth={2.2} />
        Conv 1.4% → 3.2%
      </div>
    </div>
  );
}

